import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { useToast } from './Toast';
import { useEntityPermission } from '../lib/usePermissions';
import { useIsCompact } from '../lib/useMediaQuery';

/**
 * ParcelValueContextsPanel — phase-in and 299(c) on the parcel value profile.
 *
 * ⚠️ PLACEMENT (part 2 §1.3). This was the standalone "Phase-in" tab on the parcel
 * profile. Phase-in stops being its own tab: it moves to the parcel value, into
 * the same slot the 299(c) information occupies — and that slot is THIS component,
 * because a freeze (GA 299(c)) and a phase-in were already one object here,
 * discriminated by `kind`, not two surfaces that needed merging. Phase-in, 299(c)
 * and exemptions now all live at the parcel value together (§3.5).
 *
 * Schedules are read per PARCEL, not per value row, and are deliberately NOT
 * filtered to the value's tax year: a phase-in ramp spans [start_year, end_year]
 * and a freeze covers multiple years, so scoping the list to one year would hide
 * the window that gives each row its meaning. The value's year seeds the new-row
 * form instead.
 *
 * Wires the REAL phase-in schedule write path (src/routes/phase-in.js, backlog
 * 10.9), which is what makes the dormant Domain-10 f9 phase-in calc reachable:
 *   - GET    /api/parcels/:parcelId/phase-in-schedules   load the schedules
 *   - POST   /api/parcels/:parcelId/phase-in-schedules   create a schedule
 *   - PUT    /api/phase-in-schedules/:id                 edit a schedule
 *   - DELETE /api/phase-in-schedules/:id                 delete a schedule
 *
 * A schedule ramps a parcel's appraised base toward full market across
 * [start_year, end_year]; the tax_record calc reads it (calc-caps.resolvePhaseInBase)
 * so a new/edited schedule immediately drives that year's taxable value.
 *
 * Money-redaction is respected transparently: the money-blind role receives the
 * dollar columns stripped from the guarded res.json (they render as "—").
 */

type ContextKind = 'phase_in' | 'freeze';

interface PhaseInSchedule {
  id: string;
  parcel_id: string;
  tax_year: number;
  kind: ContextKind;
  phase_in_value: number | null;
  target_market_value: number | null;
  base_value: number | null;
  start_year: number | null;
  end_year: number | null;
}

interface ScheduleForm {
  tax_year: string;
  kind: ContextKind;
  phase_in_value: string;
  target_market_value: string;
  base_value: string;
  start_year: string;
  end_year: string;
}

const EMPTY_FORM: ScheduleForm = {
  tax_year: '', kind: 'phase_in', phase_in_value: '', target_market_value: '', base_value: '', start_year: '', end_year: '',
};

const KIND_OPTIONS = [
  { value: 'phase_in', label: 'Phase-in (ramp to full value)' },
  { value: 'freeze', label: 'Freeze (hold at the frozen value)' },
];

function money(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function year(v: number | null | undefined): string {
  return v == null ? '—' : String(v);
}

function numOrUndef(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return err instanceof Error ? err.message : 'Request failed';
}

export function ParcelValueContextsPanel({ parcelId, taxYear }: { parcelId: string; taxYear: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const canView = useEntityPermission('parcel', 'can_view');
  const canAdd = useEntityPermission('parcel', 'can_add');
  const canEdit = useEntityPermission('parcel', 'can_edit');
  const canDelete = useEntityPermission('parcel', 'can_delete');
  // Editor + list side-by-side is too tight for a phone; stack below desktop.
  const isCompact = useIsCompact();

  const [form, setForm] = useState<ScheduleForm>({ ...EMPTY_FORM, tax_year: String(taxYear) });
  const [editingId, setEditingId] = useState<string | null>(null);

  const key = ['phase-in-schedules', parcelId];
  const schedulesQuery = useQuery<PhaseInSchedule[]>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<PhaseInSchedule[]>(`/api/parcels/${parcelId}/phase-in-schedules`, signal),
    enabled: !!parcelId && canView,
  });

  const resetForm = () => { setForm({ ...EMPTY_FORM, tax_year: String(taxYear) }); setEditingId(null); };

  function buildBody() {
    const isFreeze = form.kind === 'freeze';
    return {
      tax_year: numOrUndef(form.tax_year),
      kind: form.kind,
      // A freeze holds the appraised base at base_value; the ramp-only fields are
      // sent null so switching phase_in → freeze on an edit can't leave a stale ramp.
      phase_in_value: isFreeze ? null : numOrUndef(form.phase_in_value),
      target_market_value: isFreeze ? null : numOrUndef(form.target_market_value),
      base_value: numOrUndef(form.base_value),
      start_year: numOrUndef(form.start_year),
      end_year: numOrUndef(form.end_year),
    };
  }

  const saveMutation = useMutation<PhaseInSchedule, Error>({
    mutationFn: () => (editingId
      ? api.put<PhaseInSchedule>(`/api/phase-in-schedules/${editingId}`, buildBody())
      : api.post<PhaseInSchedule>(`/api/parcels/${parcelId}/phase-in-schedules`, buildBody())),
    onSuccess: () => {
      toast({ variant: 'success', message: editingId ? 'Phase-in schedule updated.' : 'Phase-in schedule created.' });
      resetForm();
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const deleteMutation = useMutation<{ deleted: boolean }, Error, string>({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/phase-in-schedules/${id}`),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Phase-in schedule deleted.' });
      if (editingId) resetForm();
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  function beginEdit(s: PhaseInSchedule) {
    setEditingId(s.id);
    setForm({
      tax_year: String(s.tax_year),
      kind: s.kind === 'freeze' ? 'freeze' : 'phase_in',
      phase_in_value: s.phase_in_value != null ? String(s.phase_in_value) : '',
      target_market_value: s.target_market_value != null ? String(s.target_market_value) : '',
      base_value: s.base_value != null ? String(s.base_value) : '',
      start_year: s.start_year != null ? String(s.start_year) : '',
      end_year: s.end_year != null ? String(s.end_year) : '',
    });
  }

  if (!canView) {
    return <Notice>You do not have permission to view phase-in schedules for this parcel.</Notice>;
  }

  const schedules = schedulesQuery.data ?? [];
  const canWrite = editingId ? canEdit : canAdd;
  const isFreeze = form.kind === 'freeze';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--sp-4)', alignItems: 'start' }}>
      {/* ── Left: editor ── */}
      <Card label={editingId ? 'Edit value context' : 'New value context'}>
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
          {isFreeze ? (
            <>A <strong>freeze</strong> holds this year&rsquo;s appraised base at the <strong>frozen value</strong> (GA 299(c)) &mdash;
            it overrides the county&rsquo;s assessment for the covered year. Add one row per covered year.</>
          ) : (
            <>A <strong>phase-in</strong> ramps the appraised base toward full market across the window. Provide an explicit
            <strong> phased value</strong> for the year, or a <strong>target</strong> (with base + window) to ramp linearly. The tax-record
            calc reads this to derive the year&rsquo;s taxable value.</>
          )}
        </p>
        <FieldRow label="Kind">
          <Select options={KIND_OPTIONS} value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as ContextKind }))} disabled={!canWrite} />
        </FieldRow>
        <FieldRow label="Tax year">
          <Input type="number" value={form.tax_year} onChange={(e) => setForm((f) => ({ ...f, tax_year: e.target.value }))} placeholder="e.g. 2026" disabled={!canWrite} />
        </FieldRow>
        {!isFreeze && (
          <FieldRow label="Phased value for this year ($, explicit step — wins over the ramp)">
            <Input type="number" value={form.phase_in_value} onChange={(e) => setForm((f) => ({ ...f, phase_in_value: e.target.value }))} placeholder="e.g. 600000" disabled={!canWrite} />
          </FieldRow>
        )}
        {!isFreeze && (
          <FieldRow label="Target market value ($, ramp destination)">
            <Input type="number" value={form.target_market_value} onChange={(e) => setForm((f) => ({ ...f, target_market_value: e.target.value }))} placeholder="e.g. 1000000" disabled={!canWrite} />
          </FieldRow>
        )}
        <FieldRow label={isFreeze ? 'Frozen value ($, the appraised base held for every covered year)' : 'Base value ($, ramp floor)'}>
          <Input type="number" value={form.base_value} onChange={(e) => setForm((f) => ({ ...f, base_value: e.target.value }))} placeholder={isFreeze ? 'e.g. 500000' : 'e.g. 300000'} disabled={!canWrite} />
        </FieldRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <FieldRow label="Start year">
            <Input type="number" value={form.start_year} onChange={(e) => setForm((f) => ({ ...f, start_year: e.target.value }))} placeholder="2025" disabled={!canWrite} />
          </FieldRow>
          <FieldRow label="End year">
            <Input type="number" value={form.end_year} onChange={(e) => setForm((f) => ({ ...f, end_year: e.target.value }))} placeholder="2027" disabled={!canWrite} />
          </FieldRow>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', flexWrap: 'wrap' }}>
          {canWrite && (
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
              {editingId ? 'Update schedule' : 'Create schedule'}
            </Button>
          )}
          {editingId && (
            <Button variant="secondary" size="sm" onClick={resetForm}>Cancel</Button>
          )}
        </div>
      </Card>

      {/* ── Right: existing schedules ── */}
      <Card label="Value contexts">
        {schedulesQuery.isLoading ? (
          <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
        ) : schedules.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
            No value contexts yet. Create a phase-in or freeze on the left to drive this parcel&rsquo;s appraised base.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
            {schedules.map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: 'var(--sp-2) var(--sp-3)', border: `1px solid ${editingId === s.id ? 'var(--color-accent, var(--color-border))' : 'var(--color-border)'}`, borderRadius: 'var(--r-md)' }}>
                <div style={{ fontSize: 'var(--fs-sm)' }}>
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', marginBottom: '2px' }}>
                    <Badge variant={s.kind === 'freeze' ? 'info' : 'neutral'}>{s.kind === 'freeze' ? 'Freeze' : 'Phase-in'}</Badge>
                    <Badge variant="neutral">Tax year {s.tax_year}</Badge>
                    <span style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-xs)' }}>
                      {year(s.start_year)}–{year(s.end_year)}
                    </span>
                  </div>
                  <div style={{ color: 'var(--color-ink-3)' }}>
                    {s.kind === 'freeze'
                      ? <>Frozen value: <strong>{money(s.base_value)}</strong></>
                      : <>Phased: <strong>{money(s.phase_in_value)}</strong> · Target: {money(s.target_market_value)} · Base: {money(s.base_value)}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={() => beginEdit(s)}>Edit</Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="sm" loading={deleteMutation.isPending && deleteMutation.variables === s.id} onClick={() => deleteMutation.mutate(s.id)} aria-label="Delete schedule">Delete</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{label}</h3>
      {children}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-1)' }}>{label}</label>
      {children}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)' }}>{children}</div>
  );
}
